import { useMemo, type ComponentType } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  Search,
  FileText,
  User,
  Calendar,
  FilePlus,
  Upload,
  ClipboardList,
} from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { useClauseStatuses } from '@/hooks/use-clause-status';
import { useComplianceSections } from '@/hooks/use-compliance-sections';
import {
  STATUS_CONFIG,
  StatusPill,
  ScopeTagsBadge,
  needsManualReview,
} from '@/app/pages/compliance/components/compliance-status';
import type { ClauseStatusDetail, RemediationHint } from '@/lib/types';
import { formatDateOnly } from '@/lib/date-utils';
import { MarkCompliantButton } from '@/app/pages/compliance/components/mark-compliant-button';

type ActionType = 'needs_record' | 'needs_document' | 'fix_document' | 'manual_review' | null;

const ACTION_CONFIG: Record<
  NonNullable<ActionType>,
  { label: string; bg: string; text: string; icon: ComponentType<{ className?: string }> }
> = {
  needs_record: {
    label: 'Needs record',
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    icon: FilePlus,
  },
  needs_document: {
    label: 'Needs document',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    icon: Upload,
  },
  fix_document: {
    label: 'Fix document',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    icon: FileText,
  },
  manual_review: {
    label: 'Manual review',
    bg: 'bg-violet-100',
    text: 'text-violet-700',
    icon: ClipboardList,
  },
};

function mapHintToActionType(hint: RemediationHint): ActionType {
  const kind = hint.kind;
  if (hint.reason === 'document_wrong_category') {
    return 'fix_document';
  }
  if (kind === 'missing_record' || kind === 'incomplete_record' || kind === 'missing_role') {
    return 'needs_record';
  }
  if (kind === 'missing_document') {
    return 'needs_document';
  }
  return 'manual_review';
}

function getPrimaryActionType(clause: ClauseStatusDetail): ActionType {
  const hints = clause.remediation_hints;
  if (hints && hints.length > 0) {
    const mapped = hints.map(mapHintToActionType);
    const priority: NonNullable<ActionType>[] = [
      'fix_document',
      'needs_record',
      'needs_document',
      'manual_review',
    ];
    for (const action of priority) {
      if (mapped.includes(action)) return action;
    }
    return 'manual_review';
  }
  if (needsManualReview(clause)) return 'manual_review';
  if (clause.evidence_warning) return 'needs_document';
  if (clause.status === 'non_compliant' || clause.status === 'partially_compliant') {
    return 'manual_review';
  }
  return null;
}

const VALID_STATUS_FILTER_VALUES = [...Object.keys(STATUS_CONFIG), 'manual_review'];

function parseStatusFilter(raw: string | null): string {
  return raw && VALID_STATUS_FILTER_VALUES.includes(raw) ? raw : '';
}

function parseActionFilter(raw: string | null): ActionType {
  if (
    raw === 'needs_record' ||
    raw === 'needs_document' ||
    raw === 'fix_document' ||
    raw === 'manual_review'
  ) {
    return raw;
  }
  return null;
}

function ActionBadge({ actionType }: { actionType: ActionType }) {
  if (!actionType) return null;
  const cfg = ACTION_CONFIG[actionType];
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.text}`}
    >
      <Icon className="mr-1 h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function getSectionNumber(clauseRef: string): number {
  const match = clauseRef.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

function EvidenceCount({ clause }: { clause: ClauseStatusDetail }) {
  return (
    <div className="flex items-center gap-2">
      <span className={clause.evidence_warning ? 'text-amber-600 font-medium' : ''}>
        {clause.evidence_count ?? clause.evidence_links?.length ?? 0}
      </span>
      {clause.evidence_warning && (
        <span
          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700"
          title={clause.evidence_warning}
        >
          {clause.evidence_warning}
        </span>
      )}
    </div>
  );
}

function ClauseRow({ clause }: { clause: ClauseStatusDetail }) {
  const navigate = useNavigate();
  return (
    <tr
      className="cursor-pointer hover:bg-gray-50 transition-colors"
      onClick={() => navigate(`/compliance/clauses/${clause.id}`)}
    >
      <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900">
        {clause.clause_ref}
      </td>
      <td className="px-3 py-3 text-sm text-gray-700">{clause.clause_title}</td>
      <td className="px-3 py-3">
        <StatusPill status={clause.status} manualReview={needsManualReview(clause)} />
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <ActionBadge actionType={getPrimaryActionType(clause)} />
          <MarkCompliantButton clause={clause} stopPropagation />
        </div>
      </td>
      <td className="px-3 py-3">
        <ScopeTagsBadge tags={clause.scope_tags} />
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
        <EvidenceCount clause={clause} />
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
        {clause.assessed_by_username ?? '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
        {clause.next_review_due
          ? formatDateOnly(clause.next_review_due)
          : '—'}
      </td>
    </tr>
  );
}

function ClauseCard({ clause }: { clause: ClauseStatusDetail }) {
  const navigate = useNavigate();
  const actionType = getPrimaryActionType(clause);
  return (
    <button
      type="button"
      onClick={() => navigate(`/compliance/clauses/${clause.id}`)}
      className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">{clause.clause_ref}</p>
          <p className="mt-0.5 text-sm text-gray-700">{clause.clause_title}</p>
        </div>
        <StatusPill status={clause.status} manualReview={needsManualReview(clause)} />
      </div>

      {actionType && (
        <div className="mt-2">
          <ActionBadge actionType={actionType} />
        </div>
      )}

      {clause.scope_tags?.length > 0 && (
        <div className="mt-3">
          <ScopeTagsBadge tags={clause.scope_tags} />
        </div>
      )}

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <dt className="text-xs uppercase tracking-wider text-gray-400">
            <FileText className="inline h-3.5 w-3.5" /> Evidence
          </dt>
          <dd className="mt-0.5 text-gray-700">
            <EvidenceCount clause={clause} />
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-gray-400">Owner</dt>
          <dd className="mt-0.5 text-gray-700">
            {clause.assessed_by_username ?? '—'}
          </dd>
        </div>
        <div className="col-span-2">
          <dt className="text-xs uppercase tracking-wider text-gray-400">
            <Calendar className="inline h-3.5 w-3.5" /> Next Review
          </dt>
          <dd className="mt-0.5 text-gray-700">
            {clause.next_review_due
              ? formatDateOnly(clause.next_review_due)
              : '—'}
          </dd>
        </div>
      </dl>
    </button>
  );
}

export function SectionClausesPage() {
  const { sectionNumber } = useParams<{ sectionNumber: string }>();
  const sn = parseInt(sectionNumber ?? '0', 10);
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = parseStatusFilter(searchParams.get('status'));
  const actionFilter = parseActionFilter(searchParams.get('action'));
  const ownerFilter = searchParams.get('owner') ?? '';
  const hasGapsFilter = searchParams.get('gaps') === 'true' ? 'true' : '';

  const setQueryParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  };

  const setStatusFilter = (value: string) => {
    const valid = parseStatusFilter(value);
    setQueryParam('status', valid ? value : null);
  };

  const setActionFilter = (value: string) => {
    const valid = parseActionFilter(value);
    setQueryParam('action', valid ? valid : null);
  };

  const setOwnerFilter = (value: string) => {
    setQueryParam('owner', value || null);
  };

  const setHasGapsFilter = (checked: boolean) => {
    setQueryParam('gaps', checked ? 'true' : null);
  };

  const { data: sections } = useComplianceSections();
  const { data: allClauses, isLoading, error } = useClauseStatuses();
  const sectionTitle =
    sections?.find((section) => section.number === sn)?.name ?? `Section ${sn}`;

  const sectionClauses = useMemo(() => {
    if (!allClauses) return [];
    return allClauses.filter((c) => getSectionNumber(c.clause_ref) === sn);
  }, [allClauses, sn]);

  const filteredClauses = useMemo(() => {
    let result = sectionClauses;

    if (statusFilter) {
      if (statusFilter === 'manual_review') {
        result = result.filter((c) => needsManualReview(c));
      } else if (statusFilter === 'not_assessed') {
        result = result.filter((c) => c.status === 'not_assessed' && !needsManualReview(c));
      } else {
        result = result.filter((c) => c.status === statusFilter);
      }
    }
    if (actionFilter) {
      result = result.filter((c) => getPrimaryActionType(c) === actionFilter);
    }
    if (ownerFilter) {
      result = result.filter(
        (c) =>
          c.assessed_by_username &&
          c.assessed_by_username.toLowerCase().includes(ownerFilter.toLowerCase()),
      );
    }
    if (hasGapsFilter === 'true') {
      result = result.filter(
        (c) =>
          c.status === 'non_compliant' || c.status === 'partially_compliant',
      );
    }

    return result;
  }, [sectionClauses, statusFilter, actionFilter, ownerFilter, hasGapsFilter]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 rounded bg-gray-200" />
          <div className="h-64 rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load clause data. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/compliance/clauses"
          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {sn}. {sectionTitle}
          </h1>
          <p className="text-sm text-gray-500">
            {sectionClauses.length} clause{sectionClauses.length !== 1 ? 's' : ''} in
            this section
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 py-3">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All statuses</option>
              <option value="compliant">Compliant</option>
              <option value="partially_compliant">Partially compliant</option>
              <option value="non_compliant">Non-compliant</option>
              <option value="not_assessed">Not assessed</option>
              <option value="manual_review">Manual review required</option>
              <option value="not_applicable">Not applicable</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-gray-400" />
            <select
              value={actionFilter ?? ''}
              onChange={(e) => setActionFilter(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All actions</option>
              <option value="needs_record">Needs record</option>
              <option value="needs_document">Needs document</option>
              <option value="fix_document">Fix document</option>
              <option value="manual_review">Manual review</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter by owner…"
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={hasGapsFilter === 'true'}
              onChange={(e) => setHasGapsFilter(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Has gaps
          </label>
        </CardContent>
      </Card>

      {/* Table */}
      {filteredClauses.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
          <Search className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No clauses found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {sectionClauses.length === 0
              ? 'No clauses are available for this section.'
              : 'Try adjusting your filters to see more results.'}
          </p>
        </div>
      ) : (
        <>
          {/* Stacked cards on narrow screens (≤480px) */}
          <div className="space-y-3 min-[481px]:hidden">
            {filteredClauses.map((clause) => (
              <ClauseCard key={clause.id} clause={clause} />
            ))}
          </div>

          {/* Table on wider screens */}
          <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white min-[481px]:block">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Clause
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Title
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Action
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Scope
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <FileText className="inline h-3.5 w-3.5" /> Evidence
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Owner
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <Calendar className="inline h-3.5 w-3.5" /> Next Review
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredClauses.map((clause) => (
                  <ClauseRow key={clause.id} clause={clause} />
                ))}
              </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
